'use client';

/**
 * Unified floating assistant popup (issue #370). ONE launcher (the chat icon)
 * opens a single popup with two modes:
 *   • AI    — the StocksIQPro AI day-trading assistant (VIP + admin only).
 *   • Support — live human support chat (everyone, incl. guests).
 *
 * The popup defaults to AI mode when the user is eligible, with a header toggle
 * to switch to Support. Users without AI access (free/guests) get Support only,
 * with no toggle. The AI engine uses the async transport (instant POST → poll
 * small slices) so it works on networks that corrupt slow/chunked responses.
 *
 * This component replaces the former standalone <ChatWidget/> (support) and the
 * separate AI launcher — they are now one surface.
 */

import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Bot, Send, Loader2, X, Sparkles, Wrench, ShieldCheck, Trash2, BarChart3, Square, MessageCircle, Lightbulb, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ChatProvider, useChatContext } from '@/context/ChatContext';
import { useAssistantChart, useAssistantBarsRef } from '@/context/AssistantChartContext';
import { assistantStatus, assistantMessage, assistantChunk, assistantCancel, type AssistantStatus } from '@/lib/api-client';
import SupportChatBody from '@/components/chat/SupportChatBody';
import { cn } from '@/lib/utils';

type Msg = { id: string; role: 'user' | 'assistant'; text: string; tools: string[]; error?: boolean };
type Mode = 'ai' | 'support';
let _i = 0;
const nid = () => `a${++_i}_${Date.now()}`;

// Default size = the minimum (the popup can grow from here, not shrink below).
const DEFAULT_W = 380;
const DEFAULT_H = 560;
const MIN_W = 340;
const MIN_H = 460;

/** Top-10 prefilled questions a trader commonly asks about a stock. Ticker-aware;
 *  the on-screen chart bars are attached automatically when one is sent. */
function quickQuestionsFor(tk: string): string[] {
  return [
    `What's the best entry price for ${tk} right now?`,
    `Where should I place my stop loss on ${tk}?`,
    `What price targets make sense for ${tk}?`,
    `Is ${tk} a buy, hold, or avoid at the current price?`,
    `What are the key support and resistance levels for ${tk}?`,
    `Is ${tk} overbought or oversold right now?`,
    `How does ${tk}'s volume today compare to its average?`,
    `What's the risk/reward of entering ${tk} here?`,
    `Should I enter ${tk} now, or wait for a pullback?`,
    `What's the trend on ${tk} across intraday, daily, and weekly?`,
  ];
}

function AssistantWidgetInner() {
  const { user, isAdmin } = useAuth();
  const isVipOrAdmin = !!user && (isAdmin || user.subscriptionTier === 'vip');
  const pathname = usePathname();
  const onAdminPath = pathname === '/admin' || pathname.startsWith('/admin/');
  const { settings, settingsLoaded, setPanelOpen, unreadCount } = useChatContext();
  const chart = useAssistantChart();
  const barsRef = useAssistantBarsRef();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('ai');
  const [status, setStatus] = useState<AssistantStatus | null>(null);
  const [adminMode, setAdminMode] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  /** Id of the assistant bubble currently being streamed into, so the working
   *  indicator attaches to it and not to older messages. */
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: DEFAULT_W, h: DEFAULT_H });
  const [isMobile, setIsMobile] = useState(false);
  // Visual-viewport box (the area NOT covered by the on-screen keyboard). Used to
  // keep the full-screen mobile popup within view while typing.
  const [vv, setVv] = useState<{ top: number; height: number } | null>(null);
  const sessionRef = useRef<string | undefined>(undefined);
  const endRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef(false);
  const jobIdRef = useRef<string | null>(null);
  const questionsRef = useRef<HTMLDivElement>(null);

  const aiEligible = isVipOrAdmin;
  // Availability is driven by the two ENABLE flags (admin config):
  //  • AI    — the user is eligible AND the AI feature is enabled (status.enabled).
  //  • Support — live chat is enabled (settings.enabled) and we're not on /admin.
  // Whichever is the ONLY one enabled becomes the default and hides the toggle;
  // when both are on, the toggle shows and AI is the default. (The old admin
  // "hide bubble" pref is intentionally not consulted — the launcher is the AI
  // assistant now, not a customer bubble, so there's nothing to hide.)
  const aiAvailable = aiEligible && !!status?.enabled;
  const supportAvailable = settingsLoaded && !!settings?.enabled && !onAdminPath;
  // The mode actually rendered. `mode` is the user's toggle intent; `effMode`
  // resolves it against availability so users only ever see an enabled mode (and
  // there's no post-load empty flash).
  const effMode: Mode = !aiAvailable ? 'support' : !supportAvailable ? 'ai' : mode;

  // AI conversation is remembered CLIENT-SIDE only (per user, auto-purged > 1 day).
  const storageKey = user ? `siq_ai_chat_${user.id}` : null;
  const posKey = user ? `siq_ai_chat_pos_${user.id}` : null;
  const adminKey = user ? `siq_ai_admin_${user.id}` : null;
  const sizeKey = user ? `siq_ai_chat_size_${user.id}` : null;
  const loadedRef = useRef(false);
  const MAX_AGE_MS = 24 * 60 * 60 * 1000;

  // Track the phone breakpoint — the popup goes full-screen (and locks page
  // scroll) on mobile, but stays a floating draggable card on desktop.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // Lock background scroll while the full-screen popup is open on mobile, so the
  // page doesn't scroll behind it while the user types.
  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, isMobile]);

  // Track the visual viewport so the full-screen mobile popup shrinks to the area
  // ABOVE the on-screen keyboard instead of being pushed up (top off-screen) when
  // an input is focused. Keyboard closed → full screen; open → fits above keyboard.
  useEffect(() => {
    if (!open || !isMobile || typeof window === 'undefined') return;
    const v = window.visualViewport;
    if (!v) return;
    const update = () => setVv({ top: v.offsetTop, height: v.height });
    update();
    v.addEventListener('resize', update);
    v.addEventListener('scroll', update);
    return () => {
      v.removeEventListener('resize', update);
      v.removeEventListener('scroll', update);
    };
  }, [open, isMobile]);

  // Fetch AI status EAGERLY (eligible users) so we know whether the AI feature is
  // enabled/configured BEFORE the popup opens — it drives the launcher, the default
  // mode, and whether the AI/Support toggle shows.
  useEffect(() => {
    if (!aiEligible) return;
    assistantStatus().then(setStatus).catch(() => { /* retried on open below */ });
  }, [aiEligible]);

  // Retry on open if the eager fetch failed; surface the error then.
  useEffect(() => {
    if (open && aiEligible && !status) {
      assistantStatus().then(setStatus).catch(() => setError('Could not load the assistant. Try reopening.'));
    }
  }, [open, aiEligible, status]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, effMode]);

  // Close the quick-questions dropdown on an outside click.
  useEffect(() => {
    if (!questionsOpen) return;
    const onDown = (e: PointerEvent) => {
      if (questionsRef.current && !questionsRef.current.contains(e.target as Node)) setQuestionsOpen(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [questionsOpen]);

  // Keep the support engine (ChatContext WS/polling) live only while the user is
  // actually viewing Support, so AI-mode users don't open a needless socket.
  useEffect(() => {
    setPanelOpen(open && effMode === 'support');
  }, [open, effMode, setPanelOpen]);

  // "Contact support" links (footer/disclaimer) fire an `open-chat` event — open
  // the popup straight into Support mode (effMode falls back if AI-only).
  useEffect(() => {
    const handler = () => { setMode('support'); setOpen(true); };
    window.addEventListener('open-chat', handler);
    return () => window.removeEventListener('open-chat', handler);
  }, []);

  // Restore AI chat on mount; drop if stale (> 1 day).
  useEffect(() => {
    if (!storageKey || loadedRef.current) return;
    loadedRef.current = true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as { messages?: Msg[]; sessionId?: string; savedAt?: number };
      if (!saved.savedAt || Date.now() - saved.savedAt > MAX_AGE_MS) {
        localStorage.removeItem(storageKey);
        return;
      }
      if (Array.isArray(saved.messages)) setMessages(saved.messages);
      if (saved.sessionId) sessionRef.current = saved.sessionId;
    } catch { /* ignore corrupt/unavailable storage */ }
  }, [storageKey, MAX_AGE_MS]);

  // Persist AI chat on change (last 40 messages; refreshes the 1-day timer).
  useEffect(() => {
    if (!storageKey || !loadedRef.current) return;
    try {
      if (messages.length === 0) { localStorage.removeItem(storageKey); return; }
      localStorage.setItem(storageKey, JSON.stringify({
        messages: messages.slice(-40), sessionId: sessionRef.current, savedAt: Date.now(),
      }));
    } catch { /* quota / unavailable — ignore */ }
  }, [messages, storageKey]);

  // Remember the dragged position (per user), clamped to the current viewport.
  useEffect(() => {
    if (!posKey) return;
    try {
      const raw = localStorage.getItem(posKey);
      if (!raw) return;
      const p = JSON.parse(raw) as { x?: number; y?: number };
      if (typeof p.x === 'number' && typeof p.y === 'number') {
        setPos({
          x: Math.max(8, Math.min(p.x, window.innerWidth - 120)),
          y: Math.max(8, Math.min(p.y, window.innerHeight - 120)),
        });
      }
    } catch { /* ignore */ }
  }, [posKey]);
  useEffect(() => {
    if (!posKey || !pos) return;
    try { localStorage.setItem(posKey, JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos, posKey]);

  // Remember the admin-mode toggle (per user) across navigation/reloads.
  useEffect(() => {
    if (!adminKey) return;
    try { if (localStorage.getItem(adminKey) === '1') setAdminMode(true); } catch { /* ignore */ }
  }, [adminKey]);
  useEffect(() => {
    if (!adminKey) return;
    try { localStorage.setItem(adminKey, adminMode ? '1' : '0'); } catch { /* ignore */ }
  }, [adminMode, adminKey]);

  // Remember the resized popup size (per user); never below the minimum.
  useEffect(() => {
    if (!sizeKey) return;
    try {
      const raw = localStorage.getItem(sizeKey);
      if (!raw) return;
      const s = JSON.parse(raw) as { w?: number; h?: number };
      if (typeof s.w === 'number' && typeof s.h === 'number') {
        setSize({ w: Math.max(MIN_W, s.w), h: Math.max(MIN_H, s.h) });
      }
    } catch { /* ignore */ }
  }, [sizeKey]);
  useEffect(() => {
    if (!sizeKey) return;
    try { localStorage.setItem(sizeKey, JSON.stringify(size)); } catch { /* ignore */ }
  }, [size, sizeKey]);

  // Nothing to show: neither AI nor Support is available to this user.
  if (!aiAvailable && !supportAvailable) return null;

  const ready = !!status?.enabled && !!status?.configured;
  const showModeToggle = aiAvailable && supportAvailable;

  const clearChat = () => {
    setMessages([]);
    sessionRef.current = undefined;
    setError(null);
    if (storageKey) { try { localStorage.removeItem(storageKey); } catch { /* ignore */ } }
  };

  // Stop an in-flight AI request: halt the client poll AND abort the backend turn.
  const cancel = () => {
    cancelRef.current = true;
    const id = jobIdRef.current;
    if (id) void assistantCancel(id).catch(() => { /* ignore */ });
  };

  // Drag the panel by its header (ignores clicks on the header buttons).
  const onHeaderPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const move = (ev: PointerEvent) => {
      const w = panel.offsetWidth;
      const h = panel.offsetHeight;
      setPos({
        x: Math.max(8, Math.min(ev.clientX - offX, window.innerWidth - w - 8)),
        y: Math.max(8, Math.min(ev.clientY - offY, window.innerHeight - h - 8)),
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  // Resize from the bottom-right grip. Pins the top-left and grows down-right;
  // never below the minimum (the default) size.
  const onResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = rect.width;
    const startH = rect.height;
    setPos({ x: rect.left, y: rect.top });
    const move = (ev: PointerEvent) => {
      setSize({
        w: Math.max(MIN_W, Math.min(startW + (ev.clientX - startX), window.innerWidth - rect.left - 8)),
        h: Math.max(MIN_H, Math.min(startH + (ev.clientY - startY), window.innerHeight - rect.top - 8)),
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const send = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setError(null);
    setInput('');
    setBusy(true);
    cancelRef.current = false;
    setMessages((p) => [...p, { id: nid(), role: 'user', text, tools: [] }]);
    const aid = nid();
    setMessages((p) => [...p, { id: aid, role: 'assistant', text: '', tools: [] }]);
    setStreamingId(aid);
    const patch = (fn: (m: Msg) => Msg) => setMessages((p) => p.map((m) => (m.id === aid ? fn(m) : m)));

    try {
      const sendCtx = chart
        ? { ticker: chart.ticker, timeframe: chart.timeframe, bars: barsRef.current ?? undefined }
        : undefined;
      const { id } = await assistantMessage(text, sessionRef.current, adminMode, sendCtx);
      jobIdRef.current = id;
      let full = '';
      let offset = 0;
      let toolCount = 0;
      let errored = false;
      let netFails = 0;
      // Bound NO-PROGRESS time by wall clock rather than a poll count: the poll
      // interval varies, and a Codex turn can legitimately spend minutes on
      // tool calls before emitting anything.
      const NO_PROGRESS_MS = 10 * 60 * 1000;
      const MAX_NET_FAILS = 8;
      let lastProgressAt = Date.now();
      // Poll fast (120ms) while text is flowing so the answer streams in live;
      // back off (450ms) while the model is still thinking with nothing to show.
      for (;;) {
        if (cancelRef.current) break;

        let c: Awaited<ReturnType<typeof assistantChunk>>;
        try {
          c = await assistantChunk(id, offset);
          netFails = 0;
        } catch (netErr) {
          // A dropped poll is not a dead turn — the job keeps running server
          // side, and its text is still retrievable from `offset`. Mobile
          // networks drop requests routinely, and one "Failed to fetch" used
          // to abandon a turn that was working. Retry with backoff instead.
          if (cancelRef.current) break;
          if (++netFails > MAX_NET_FAILS) throw netErr;
          await new Promise((r) => setTimeout(r, Math.min(1000 * netFails, 4000)));
          continue;
        }

        if (c.sessionId) sessionRef.current = c.sessionId;
        if (Array.isArray(c.tools) && c.tools.length > toolCount) {
          const tools = c.tools as string[];
          patch((m) => ({ ...m, tools }));
          toolCount = tools.length;
          // Tool activity is progress even when no text has been produced yet.
          lastProgressAt = Date.now();
        }
        if (c.chunk) {
          full += c.chunk;
          patch((m) => ({ ...m, text: full }));
          offset = c.next ?? (offset + c.chunk.length);
          lastProgressAt = Date.now();
        } else if (Date.now() - lastProgressAt > NO_PROGRESS_MS) {
          throw new Error('Timed out waiting for the assistant.');
        }
        if (c.status === 'error') {
          errored = true;
          const em = String(c.error ?? 'Assistant error');
          setError(em);
          patch((m) => ({ ...m, text: full.trim() ? `${full}\n\n⚠ ${em}` : `⚠ ${em}`, error: true }));
          break;
        }
        if (c.done) break;
        await new Promise((r) => setTimeout(r, c.chunk ? 120 : 450));
      }
      if (cancelRef.current) patch((m) => ({ ...m, text: full.trim() ? `${full} ⏹` : '⏹ Stopped.' }));
      else if (!errored && !full.trim()) patch((m) => ({ ...m, text: '(no response)' }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Request failed';
      setError(msg);
      patch((m) => ({ ...m, text: `⚠ ${msg}`, error: true }));
    } finally {
      setBusy(false);
      setStreamingId(null);
    }
  };

  return (
    <>
      {!open && (
        // Matches the former support ChatBubble: small on mobile (p-2.5), 44px on
        // desktop — so it doesn't dwarf the scroll-up / donate buttons on phones.
        <button
          onClick={() => setOpen(true)}
          title="Chat & AI Assistant"
          aria-label="Open assistant"
          className="chat-bubble-float fixed bottom-6 right-6 z-40 p-2.5 rounded-full shadow-lg transition-all bg-sky-900/50 hover:bg-sky-800/70 text-sky-300 backdrop-blur-sm lg:w-11 lg:h-11 lg:bg-sky-700/80 lg:hover:bg-sky-600/90 lg:text-white lg:flex lg:items-center lg:justify-center"
        >
          <MessageCircle size={18} />
          {supportAvailable && unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          ref={panelRef}
          className={cn(
            'fixed z-50 flex flex-col bg-card-bg border border-default rounded-2xl shadow-2xl overflow-hidden',
            // Desktop: floating card. Mobile: full-screen via inline style (below).
            !isMobile && !pos && 'bottom-24 right-6',
          )}
          style={isMobile
            // Mobile: pin to the visual viewport so the popup stays fully on screen
            // when the keyboard opens (full screen when it's closed).
            ? (vv
                ? { left: 8, right: 8, top: vv.top + 8, height: vv.height - 16 }
                : { left: 8, right: 8, top: 8, bottom: 8 })
            : {
                width: `min(92vw, ${size.w}px)`,
                height: `min(85vh, ${size.h}px)`,
                ...(pos ? { left: pos.x, top: pos.y } : {}),
              }}
        >
          {/* Header — drag handle */}
          <div onPointerDown={onHeaderPointerDown} className="flex items-center gap-2 px-3 py-2 border-b border-default cursor-move select-none touch-none">
            <div className="w-7 h-7 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
              {effMode === 'ai' ? <Bot size={16} className="text-sky-400" /> : <MessageCircle size={16} className="text-sky-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white leading-tight">{effMode === 'ai' ? 'AI Assistant' : 'Support'}</div>
              <div className="text-[10px] text-fg-subtle truncate">
                {effMode === 'ai' ? (adminMode ? 'Admin mode · full tools' : 'Market data') : 'Live chat with our team'}
              </div>
            </div>

            {/* Single mode toggle: shows the mode you'd switch TO (in AI → offer
                Support; in Support → offer AI). Label always visible incl. mobile. */}
            {showModeToggle && (
              <button
                onClick={() => setMode(effMode === 'ai' ? 'support' : 'ai')}
                title={effMode === 'ai' ? 'Switch to live support chat' : 'Switch to the AI assistant'}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-card-hover-bg text-fg-muted hover:text-white transition-colors flex-shrink-0"
              >
                {effMode === 'ai' ? <MessageCircle size={12} /> : <Sparkles size={12} />}
                {effMode === 'ai' ? 'Support' : 'AI'}
              </button>
            )}

            {effMode === 'ai' && status?.isAdmin && (
              <button
                onClick={() => setAdminMode((v) => !v)}
                title="Toggle admin mode (full tools incl. SQL)"
                className={cn(
                  'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors flex-shrink-0',
                  adminMode ? 'bg-amber-500/15 text-amber-400' : 'bg-card-hover-bg text-fg-muted hover:text-white',
                )}
              >
                <ShieldCheck size={12} /> <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            {effMode === 'ai' && messages.length > 0 && (
              <button onClick={clearChat} title="Clear chat" className="p-1 rounded text-fg-subtle hover:text-white hover:bg-card-hover-bg flex-shrink-0">
                <Trash2 size={14} />
              </button>
            )}
            <button onClick={() => setOpen(false)} title="Close" className="p-1 rounded text-fg-subtle hover:text-white hover:bg-card-hover-bg flex-shrink-0">
              <X size={16} />
            </button>
          </div>

          {/* ─────────────── AI MODE ─────────────── */}
          {effMode === 'ai' && aiAvailable && (
            <>
              {/* AI mode only renders when enabled, so the only not-ready case left
                  is "enabled but not configured" (no token). */}
              {status && !status.configured && (
                <div className="px-3 py-2 text-[11px] text-amber-300 bg-amber-500/10 border-b border-amber-500/20">
                  The assistant is not configured yet.
                  {supportAvailable && <> You can still <button onClick={() => setMode('support')} className="underline hover:text-amber-200">contact support</button>.</>}
                </div>
              )}
              {error && (
                <div className="px-3 py-1.5 text-[11px] text-red-400 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
                  <span className="flex-1 break-words">{error}</span>
                  <button onClick={() => setError(null)} className="hover:text-red-300">&times;</button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-fg-subtle gap-2 px-4">
                    <Bot size={24} className="text-slate-600" />
                    <p className="text-xs">Ask about a stock — signal, quote, fundamentals, recent flips.</p>
                    <p className="text-[10px] text-slate-600">e.g. “What&apos;s the signal for NVDA?”</p>
                  </div>
                ) : (
                  messages.map((m) => (
                    <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                      <div
                        className={cn(
                          'max-w-[85%] rounded-2xl px-3 py-1.5',
                          m.role === 'user'
                            ? 'bg-sky-600 text-white rounded-br-md'
                            : cn('bg-card-hover-bg text-slate-200 rounded-bl-md', m.error && 'border border-red-500/30'),
                        )}
                      >
                        {m.tools.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {m.tools.map((t, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-[9px] bg-slate-700/60 text-sky-300 rounded px-1 py-0.5">
                                <Wrench size={9} /> {t}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs whitespace-pre-wrap break-words">
                          {m.text}
                        </p>
                        {/* Keep a live indicator for as long as the turn runs —
                            NOT only while the bubble is empty. The Codex route
                            emits completed items rather than token deltas, so
                            after the model's opening line there can be minutes
                            of silence while it runs tools. Dropping the spinner
                            the moment the first text lands makes a working turn
                            look dead. */}
                        {m.id === streamingId && busy && (
                          <span className={cn(
                            'inline-flex items-center gap-1.5 text-fg-subtle',
                            m.text ? 'mt-1 text-[10px]' : 'text-xs',
                          )}>
                            <Loader2 size={m.text ? 10 : 12} className="animate-spin" />
                            {m.tools.length > 0 ? 'Running tools…' : m.text ? 'Working…' : 'Thinking…'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={endRef} />
              </div>

              {chart && ready && (
                <div className="flex items-center gap-2 px-2 py-1 border-t border-default">
                  <span className="inline-flex items-center gap-1 text-[10px] bg-sky-500/15 text-sky-300 rounded px-1.5 py-0.5">
                    <BarChart3 size={10} /> {chart.ticker}{chart.timeframe ? ` · ${chart.timeframe}` : ''}
                  </span>
                  <button
                    onClick={() => void send(`Day-trade read on ${chart.ticker} (${chart.timeframe || 'current'} chart): is now a good entry? Use the intraday bars — give a clear call with entry, stop, and target levels plus key intraday support/resistance.`)}
                    disabled={busy}
                    className="text-[10px] font-semibold text-sky-400 hover:text-sky-300 disabled:opacity-50"
                  >
                    Analyze entry →
                  </button>

                  {/* Right-aligned quick-questions menu (prefilled, ticker-aware). */}
                  <div ref={questionsRef} className="ml-auto relative">
                    <button
                      type="button"
                      onClick={() => setQuestionsOpen((v) => !v)}
                      disabled={busy}
                      className="flex items-center gap-1 text-[10px] font-semibold text-sky-400 hover:text-sky-300 disabled:opacity-50"
                      title="Common questions to ask"
                    >
                      <Lightbulb size={11} /> Questions <ChevronDown size={10} className={cn('transition-transform', questionsOpen && 'rotate-180')} />
                    </button>
                    {questionsOpen && (
                      <div className="absolute bottom-full right-0 mb-1.5 w-64 max-h-72 overflow-y-auto bg-card-bg border border-default rounded-lg shadow-2xl z-20 py-1">
                        {quickQuestionsFor(chart.ticker).map((q, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => { setQuestionsOpen(false); void send(q); }}
                            className="block w-full text-left px-3 py-1.5 text-[11px] text-slate-200 hover:bg-card-hover-bg hover:text-white"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); void send(); }} className="flex gap-1.5 p-2 border-t border-default">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={ready ? 'Ask the assistant…' : 'Unavailable'}
                  disabled={busy || !ready}
                  className="flex-1 bg-surface border border-default rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500 disabled:opacity-50"
                />
                {busy ? (
                  <button
                    type="button"
                    onClick={cancel}
                    title="Stop"
                    className="px-2.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg flex items-center"
                  >
                    <Square size={12} className="fill-current" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!input.trim() || !ready}
                    className="px-2.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white rounded-lg flex items-center"
                  >
                    <Send size={14} />
                  </button>
                )}
              </form>
            </>
          )}

          {/* ─────────────── SUPPORT MODE ─────────────── */}
          {effMode === 'support' && supportAvailable && (
            <SupportChatBody onClose={() => setOpen(false)} />
          )}

          {/* Resize grip (bottom-right) — desktop only; on mobile the popup is
              full-screen and the grip would sit on top of the Send button. */}
          <div
            onPointerDown={onResizePointerDown}
            title="Drag to resize"
            className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize z-10 hidden sm:flex items-end justify-end p-[3px] touch-none"
          >
            <div className="w-1.5 h-1.5 border-r-2 border-b-2 border-fg-subtle/60" />
          </div>
        </div>
      )}
    </>
  );
}

export default function AiAssistantWidget() {
  return (
    <ChatProvider>
      <AssistantWidgetInner />
    </ChatProvider>
  );
}
